// src/pages/admin/AdminDashboard.tsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Users,
  Shield,
  FileText,
  CreditCard,
  TrendingUp,
  Clock,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Sparkles,
  BarChart3,
  Target,
  Zap,
  ShieldCheck,
  Eye,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "../../components/LoadingScreen";

const AdminDashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingKyc: 0,
    totalTransactions: 0,
    pendingDeposits: 0,
    pendingWithdrawals: 0,
    totalRecoveryCases: 0,
    activeRecoveryCases: 0,
    dailyGrowth: 8,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setStats({
          totalUsers: 1254,
          activeUsers: 342,
          pendingKyc: 23,
          totalTransactions: 8456,
          pendingDeposits: 15,
          pendingWithdrawals: 8,
          totalRecoveryCases: 167,
          activeRecoveryCases: 42,
          dailyGrowth: 8,
        });
      }, 1000);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading admin dashboard..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Admin Dashboard
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Welcome back, Admin {user?.first_name}. Here's your platform
                  overview.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-200/30 dark:border-gray-800/30">
              <div className="h-2 w-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Platform Active
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Users Card */}
        <Link
          to="/admin/users"
          className={`${GlobalStyles.card} group hover:border-primary/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-400" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Total Users
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.totalUsers.toLocaleString()}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {stats.activeUsers} active today
              </p>
              <span className="text-xs font-medium text-emerald-500">
                +{stats.dailyGrowth}%
              </span>
            </div>
          </div>
        </Link>

        {/* KYC Card */}
        <Link
          to="/admin/kyc"
          className={`${GlobalStyles.card} group hover:border-amber-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-400/10">
              <ShieldCheck className="h-6 w-6 text-amber-500" />
            </div>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
              Review
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Pending KYC
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.pendingKyc}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-1.5">
              <div
                className="bg-gradient-to-r from-amber-400 to-amber-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(stats.pendingKyc * 10, 100)}%` }}
              ></div>
            </div>
          </div>
        </Link>

        {/* Transactions Card */}
        <Link
          to="/admin/transactions"
          className={`${GlobalStyles.card} group hover:border-emerald-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-400/10">
              <CreditCard className="h-6 w-6 text-emerald-500" />
            </div>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              {stats.totalTransactions.toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Total Transactions
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.totalTransactions.toLocaleString()}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Clock className="h-3 w-3 text-amber-500" />
              <p className="text-xs text-amber-500">
                {stats.pendingDeposits + stats.pendingWithdrawals} pending
              </p>
            </div>
          </div>
        </Link>

        {/* Recovery Card */}
        <Link
          to="/admin/recovery"
          className={`${GlobalStyles.card} group hover:border-purple-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-400/10">
              <FileText className="h-6 w-6 text-purple-500" />
            </div>
            <Activity className="h-5 w-5 text-purple-400" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Active Recovery
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.activeRecoveryCases}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {stats.totalRecoveryCases} total cases
              </p>
              <span className="text-xs font-medium text-purple-500">
                {Math.round(
                  (stats.activeRecoveryCases / stats.totalRecoveryCases) * 100,
                ) || 0}
                %
              </span>
            </div>
          </div>
        </Link>
      </div>

      {/* Quick Actions & Pending Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Actions */}
        <div className={`${GlobalStyles.card}`}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <div className="space-y-2">
            <Link
              to="/admin/users"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <Users className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              <span>Manage Users</span>
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover/action:opacity-100 transition-opacity" />
            </Link>
            <Link
              to="/admin/kyc"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <Shield className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              <span>Review KYC</span>
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover/action:opacity-100 transition-opacity" />
            </Link>
            <Link
              to="/admin/recovery"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <FileText className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              <span>Recovery Cases</span>
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover/action:opacity-100 transition-opacity" />
            </Link>
            <Link
              to="/admin/transactions"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <CreditCard className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              <span>Transactions</span>
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover/action:opacity-100 transition-opacity" />
            </Link>
          </div>
        </div>

        {/* Pending Actions */}
        <div className={`${GlobalStyles.card}`}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Pending Actions
          </h3>
          <div className="space-y-3">
            {/* Deposits */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-amber-500/10 mr-3">
                  <Clock className="h-4 w-4 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Deposits to approve
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {stats.pendingDeposits} pending
                  </p>
                </div>
              </div>
              <Link
                to="/admin/transactions?type=deposit&status=pending"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Review
              </Link>
            </div>

            {/* Withdrawals */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-rose-500/10 mr-3">
                  <AlertCircle className="h-4 w-4 text-rose-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Withdrawals to process
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {stats.pendingWithdrawals} pending
                  </p>
                </div>
              </div>
              <Link
                to="/admin/transactions?type=withdrawal&status=pending"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Process
              </Link>
            </div>

            {/* KYC */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-blue-500/10 mr-3">
                  <Eye className="h-4 w-4 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    KYC to review
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {stats.pendingKyc} pending
                  </p>
                </div>
              </div>
              <Link
                to="/admin/kyc"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Verify
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className={`${GlobalStyles.card}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Recent Platform Activity
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-emerald-500/10 mr-3">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  12 new users registered today
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  +8% from yesterday
                </p>
              </div>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              2 hours ago
            </span>
          </div>
          <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-rose-500/10 mr-3">
                <XCircle className="h-4 w-4 text-rose-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  3 KYC submissions rejected
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Poor quality photos
                </p>
              </div>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              4 hours ago
            </span>
          </div>
          <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-500/10 mr-3">
                <CheckCircle className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  8 deposits confirmed
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Total: $4,568.00
                </p>
              </div>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              6 hours ago
            </span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className={`${GlobalStyles.card}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Performance Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Approval Rate
              </p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              94%
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              KYC approval success
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-emerald-500" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Response Time
              </p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              2.3h
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Average support response
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-purple-500" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Recovery Success
              </p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              87%
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Case resolution rate
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
